import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider, useI18n } from "@/context/I18nContext";
import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
import PublicVerify from "@/pages/PublicVerify";
import AuthCallback from "@/pages/AuthCallback";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Applications from "@/pages/Applications";
import ApplicationNew from "@/pages/ApplicationNew";
import ApplicationDetail from "@/pages/ApplicationDetail";
import Approvals from "@/pages/Approvals";
import Plates from "@/pages/Plates";
import Scanner from "@/pages/Scanner";
import Audit from "@/pages/Audit";
import Fraud from "@/pages/Fraud";
import Admin from "@/pages/Admin";
import Security from "@/pages/Security";
import Handover from "@/pages/Handover";
import OfflineScanner from "@/pages/OfflineScanner";
import DemoStickers from "@/pages/DemoStickers";
import DemoUsers from "@/pages/DemoUsers";
import DemoInvite from "@/pages/DemoInvite";
import PlateLookup from "@/pages/PlateLookup";
import AluminumDesigner from "@/pages/AluminumDesigner";
import Violations from "@/pages/Violations";
import PlateAlerts from "@/pages/PlateAlerts";
import OwnerPortal from "@/pages/OwnerPortal";
import CitationVerify from "@/pages/CitationVerify";
import "@/App.css";

function Protected({ children, roles }) {
    const { user, loading } = useAuth();
    const loc = useLocation();
    if (loading) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
    if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
    if (roles && !roles.includes(user.role)) {
        return (
            <Layout>
                <div className="max-w-lg mx-auto mt-20 text-center space-y-3" data-testid="forbidden-page">
                    <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· 403 · Forbidden</div>
                    <h1 className="text-3xl font-bold tracking-tight">You don't have access to this page</h1>
                    <p className="text-sm text-muted-foreground">This section is restricted to Super Admin. Your role: <span className="mono">{user.role}</span>.</p>
                </div>
            </Layout>
        );
    }
    return <Layout>{children}</Layout>;
}

function AppRouter() {
    const location = useLocation();
    // CRITICAL synchronous check: intercept OAuth callback fragment BEFORE Protected/AuthProvider run
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/verify" element={<PublicVerify />} />
            <Route path="/plate-alerts" element={<PlateAlerts />} />
            <Route path="/owner-portal" element={<OwnerPortal />} />
            <Route path="/citation/:id" element={<CitationVerify />} />
            <Route path="/demo-invite" element={<DemoInvite />} />
            <Route path="/handover" element={<Handover />} />
            <Route path="/offline" element={<OfflineScanner />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/app" element={<Protected><Dashboard /></Protected>} />
            <Route path="/app/applications" element={<Protected><Applications /></Protected>} />
            <Route path="/app/applications/new" element={<Protected><ApplicationNew /></Protected>} />
            <Route path="/app/applications/:id" element={<Protected><ApplicationDetail /></Protected>} />
            <Route path="/app/approvals" element={<Protected><Approvals /></Protected>} />
            <Route path="/app/plates" element={<Protected><Plates /></Protected>} />
            <Route path="/app/scanner" element={<Protected><Scanner /></Protected>} />
            <Route path="/app/plate-lookup" element={<Protected roles={["enforcement_officer", "super_admin", "auditor"]}><PlateLookup /></Protected>} />
            <Route path="/app/aluminum-plates" element={<Protected roles={["registrar", "printer_operator", "super_admin"]}><AluminumDesigner /></Protected>} />
            <Route path="/app/violations" element={<Protected roles={["super_admin", "auditor"]}><Violations /></Protected>} />
            <Route path="/app/audit" element={<Protected><Audit /></Protected>} />
            <Route path="/app/fraud" element={<Protected><Fraud /></Protected>} />
            <Route path="/app/admin" element={<Protected roles={["super_admin"]}><Admin /></Protected>} />
            <Route path="/app/demo-stickers" element={<Protected roles={["super_admin"]}><DemoStickers /></Protected>} />
            <Route path="/app/demo-users" element={<Protected roles={["super_admin"]}><DemoUsers /></Protected>} />
            <Route path="/app/security" element={<Protected><Security /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function AppShell() {
    const { lang } = useI18n();
    useEffect(() => { document.title = "UTAL MEA · Plate Verification Platform"; }, []);
    return (
        <BrowserRouter>
            <Toaster richColors position={lang === "ar" ? "top-left" : "top-right"} />
            <AppRouter />
        </BrowserRouter>
    );
}

export default function App() {
    return (
        <I18nProvider>
            <AuthProvider>
                <AppShell />
            </AuthProvider>
        </I18nProvider>
    );
}
